import {useCallback, useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import {
    ComponentMappingStatusSummary,
    ComponentReportMappingState,
    isRunActive,
    StartMappingRunOptions,
    MappingRunStatus,
} from '../@types/component-mapping-state';
import componentMappingStateService from '../services/component-mapping-state.service';

const POLL_INTERVAL_MS = 5000;
const MAX_PERSIST_AGE_MS = 24 * 60 * 60 * 1000;

interface PersistedRun {
    state: ComponentReportMappingState;
    savedAt: number;
}

export interface UseComponentMappingRunReturn {
    /** The latest known run state for the selected report source, or null if never run. */
    runState: ComponentReportMappingState | null;
    /**
     * Where the component stands against the selected source, derived from the mapping records.
     * Re-read when the source changes and whenever a run finishes, which are the only two moments
     * it can change from this screen.
     */
    mappingStatus: ComponentMappingStatusSummary | null;
    /** A submit or cancel request is in flight. */
    isLoading: boolean;
    /** The most recent client-side error, if any. */
    error: string | null;
    /** True while a run is QUEUED or RUNNING. */
    isActive: boolean;
    /** Submits a new run and starts polling. */
    startMappingRun: (options?: StartMappingRunOptions) => Promise<void>;
    /** Cancels the in-flight run. */
    cancelMappingRun: () => Promise<void>;
    /** Re-fetches the current run state and the derived mapping status once. */
    refresh: () => Promise<void>;
}

/**
 * Drives one Component/source mapping run: fetches the current run state and the derived mapping
 * status, submits, cancels, and polls every {@link POLL_INTERVAL_MS} while a run is active.
 * Modelled on {@code useMappingJob}, but the backend poll returns the
 * {@link ComponentReportMappingState} directly so there is no job-context translation.
 *
 * @param componentId the Component version being edited
 * @param sourceName  the currently selected report source, or null when none is selected
 */
export function useComponentMappingRun(
    componentId: number | undefined,
    sourceName: string | null,
): UseComponentMappingRunReturn {
    const [runState, setRunState] = useState<ComponentReportMappingState | null>(null);
    const [mappingStatus, setMappingStatus] = useState<ComponentMappingStatusSummary | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const pollRef = useRef<number | null>(null);
    const pollFnRef = useRef<() => Promise<void>>(async () => { /* set below */ });
    const lastStatusRef = useRef<MappingRunStatus | null>(null);
    /** Sequence number of the newest status request, so a superseded response is dropped. */
    const statusRequestRef = useRef(0);

    const storageKey = componentId != null && sourceName != null
        ? `component_mapping_run_${componentId}_${sourceName}`
        : null;

    const loadMappingStatus = useCallback(async (): Promise<void> => {
        if (componentId == null || sourceName == null) {
            setMappingStatus(null);
            return;
        }
        // Deriving a status is a counted pass over the source, so a slow one for the previously
        // selected source can land after a fast one for the current selection. Whoever asked last
        // wins: without this the panel can show one source's counts while another is selected, and
        // the Update button is enabled or disabled from the wrong numbers.
        const request = ++statusRequestRef.current;
        try {
            const status = await componentMappingStateService.getMappingStatus(componentId, sourceName);
            if (request === statusRequestRef.current) {
                setMappingStatus(status);
            }
        } catch (err) {
            // The status is a readout, not the point of the screen: a failure to read it must not
            // take the run controls down with it.
            // eslint-disable-next-line no-console
            console.error('Mapping status fetch failed:', err);
        }
    }, [componentId, sourceName]);

    const statusFnRef = useRef<() => Promise<void>>(async () => { /* set below */ });
    statusFnRef.current = loadMappingStatus;

    const stopPolling = useCallback((): void => {
        if (pollRef.current != null) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const persist = useCallback((state: ComponentReportMappingState | null): void => {
        if (!storageKey) {
            return;
        }
        try {
            if (state) {
                localStorage.setItem(storageKey, JSON.stringify({state, savedAt: Date.now()} as PersistedRun));
            } else {
                localStorage.removeItem(storageKey);
            }
        } catch {
            // localStorage unavailable (private mode, quota) -- non-fatal
        }
    }, [storageKey]);

    const applyState = useCallback((state: ComponentReportMappingState | null): void => {
        setRunState(state);
        persist(state);

        const previous = lastStatusRef.current;
        const current = state?.runStatus ?? null;
        lastStatusRef.current = current;

        // Fire a toast only on the transition into a terminal state.
        if (previous && isRunActive(previous) && current && !isRunActive(current)) {
            if (current === MappingRunStatus.COMPLETED) {
                const mapped = state?.runStatistics?.mapped ?? 0;
                toast.success(`Mapping run complete — ${mapped} report${mapped === 1 ? '' : 's'} mapped`);
            } else if (current === MappingRunStatus.ERROR) {
                toast.error(`Mapping run failed: ${state?.runError ?? 'Unknown error'}`);
            } else if (current === MappingRunStatus.CANCELLED) {
                toast.info('Mapping run cancelled');
            }
        }
        if (current && !isRunActive(current)) {
            stopPolling();
            // The run just wrote records, so the counts behind the status have moved.
            void statusFnRef.current();
        }
    }, [persist, stopPolling]);

    const poll = useCallback(async (): Promise<void> => {
        if (componentId == null || sourceName == null) {
            return;
        }
        try {
            const state = await componentMappingStateService.getMappingRunState(componentId, sourceName);
            applyState(state);
        } catch (err) {
            // A transient poll failure should not kill the loop; surface it but keep going.
            // eslint-disable-next-line no-console
            console.error('Mapping run status poll failed:', err);
        }
    }, [componentId, sourceName, applyState]);

    pollFnRef.current = poll;

    const startPolling = useCallback((): void => {
        stopPolling();
        pollRef.current = window.setInterval(() => {
            void pollFnRef.current();
        }, POLL_INTERVAL_MS);
    }, [stopPolling]);

    // Load state when the selected source changes.
    useEffect(() => {
        lastStatusRef.current = null;
        setError(null);

        if (componentId == null || sourceName == null) {
            setRunState(null);
            setMappingStatus(null);
            // Nothing selected: retire any status request still in flight for the old selection.
            statusRequestRef.current++;
            stopPolling();
            return;
        }

        // The previous source's state must not stand in for this one while its own fetch is in
        // flight: a source never run would inherit a completed run, the workbook would mount for
        // it and fail to load an overview that does not exist.
        setRunState(null);
        setMappingStatus(null);
        void loadMappingStatus();

        // Optimistic paint from localStorage while the fetch is in flight.
        if (storageKey) {
            try {
                const raw = localStorage.getItem(storageKey);
                if (raw) {
                    const parsed = JSON.parse(raw) as PersistedRun;
                    if (Date.now() - parsed.savedAt < MAX_PERSIST_AGE_MS) {
                        setRunState(parsed.state);
                        lastStatusRef.current = parsed.state.runStatus ?? null;
                    } else {
                        localStorage.removeItem(storageKey);
                    }
                }
            } catch {
                // ignore malformed cache
            }
        }

        let cancelled = false;
        componentMappingStateService.getMappingRunState(componentId, sourceName)
            .then(state => {
                if (cancelled) {
                    return;
                }
                setRunState(state);
                lastStatusRef.current = state?.runStatus ?? null;
                persist(state);
                if (isRunActive(state?.runStatus)) {
                    startPolling();
                }
            })
            .catch(err => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : String(err));
                }
            });

        return () => {
            cancelled = true;
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [componentId, sourceName]);

    // Stop polling on unmount.
    useEffect(() => stopPolling, [stopPolling]);

    const startMappingRun = useCallback(async (options?: StartMappingRunOptions): Promise<void> => {
        if (componentId == null || sourceName == null) {
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const state = await componentMappingStateService.startMappingRun(componentId, sourceName, options ?? {});
            lastStatusRef.current = state.runStatus ?? null;
            setRunState(state);
            persist(state);
            startPolling();
            toast.success('Mapping run started');
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            setError(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    }, [componentId, sourceName, persist, startPolling]);

    const cancelMappingRun = useCallback(async (): Promise<void> => {
        if (componentId == null || sourceName == null) {
            return;
        }
        setIsLoading(true);
        try {
            stopPolling();
            await componentMappingStateService.cancelMappingRun(componentId, sourceName);
            await poll();
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            setError(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    }, [componentId, sourceName, poll, stopPolling]);

    const refresh = useCallback(async (): Promise<void> => {
        await poll();
        await loadMappingStatus();
    }, [poll, loadMappingStatus]);

    return {
        runState,
        mappingStatus,
        isLoading,
        error,
        isActive: isRunActive(runState?.runStatus),
        startMappingRun,
        cancelMappingRun,
        refresh,
    };
}
